import { useState, useEffect } from "react";
import { format, isToday, isPast, isFuture } from "date-fns";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  User, 
  Package, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Truck,
  Ban
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { formatRetailerName } from "@/lib/formatRetailer";
import type { UserPickup } from "@/types/database";

interface PackageInfo {
  id: string;
  retailer: string;
  size: string;
}

interface PickupWithDetails extends UserPickup {
  packages: PackageInfo[];
  userName: string;
  userEmail: string;
}

interface PickupCompletionManagerProps {
  onPickupUpdated?: () => void;
}

export const PickupCompletionManager = ({ onPickupUpdated }: PickupCompletionManagerProps) => {
  const [pickups, setPickups] = useState<PickupWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedPickup, setSelectedPickup] = useState<PickupWithDetails | null>(null);
  const [selectedAction, setSelectedAction] = useState<string>("");
  const [cancelReason, setCancelReason] = useState("");

  const fetchConfirmedPickups = async () => {
    try {
      // Fetch confirmed pickups (ready for completion actions)
      const { data: pickupData, error: pickupError } = await (supabase as any)
        .from("user_pickups")
        .select("*")
        .eq("status", "confirmed")
        .order("proposed_date", { ascending: true });

      if (pickupError) throw pickupError;

      if (!pickupData || pickupData.length === 0) {
        setPickups([]);
        setLoading(false);
        return;
      }

      // Fetch user profiles
      const userIds = [...new Set(pickupData.map((p: UserPickup) => p.user_id))];
      const { data: profileData } = await (supabase as any)
        .from("profiles")
        .select("id, email, first_name, last_name")
        .in("id", userIds);

      const profileMap = new Map();
      (profileData || []).forEach((p: any) => {
        const firstName = p.first_name || "";
        const lastName = p.last_name || "";
        const fullName = firstName && lastName 
          ? `${firstName} ${lastName}` 
          : p.email?.split("@")[0] || "Unknown";
        profileMap.set(p.id, { name: fullName, email: p.email });
      });

      // Fetch packages linked to these pickups
      const pickupIds = pickupData.map((p: UserPickup) => p.id);
      const { data: packageData } = await (supabase as any)
        .from("packages")
        .select("id, retailer, size, pickup_id")
        .in("pickup_id", pickupIds);

      const packagesByPickup = new Map<string, PackageInfo[]>();
      (packageData || []).forEach((pkg: any) => {
        const existing = packagesByPickup.get(pkg.pickup_id) || [];
        existing.push({ id: pkg.id, retailer: pkg.retailer, size: pkg.size });
        packagesByPickup.set(pkg.pickup_id, existing);
      });

      // Combine data
      const pickupsWithDetails: PickupWithDetails[] = pickupData.map((pickup: UserPickup) => {
        const profile = profileMap.get(pickup.user_id) || { name: "Unknown", email: "" };
        return {
          ...pickup,
          packages: packagesByPickup.get(pickup.id) || [],
          userName: profile.name,
          userEmail: profile.email,
        };
      });

      setPickups(pickupsWithDetails);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error fetching confirmed pickups:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmedPickups();
  }, []);


  const handleOpenAction = (pickup: PickupWithDetails) => {
    setSelectedPickup(pickup);
    setSelectedAction("");
    setCancelReason("");
    setActionDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedPickup || !selectedAction) return;

    setActionLoading(true);
    try {
      const updateData: Record<string, any> = {
        status: selectedAction,
      };

      // Add timestamps based on action
      if (selectedAction === "completed") {
        updateData.completed_at = new Date().toISOString();
      } else if (selectedAction === "cancelled") {
        updateData.cancelled_at = new Date().toISOString();
        updateData.cancelled_reason = cancelReason || null;
      }

      // Update pickup status
      const { error } = await (supabase as any)
        .from("user_pickups")
        .update(updateData)
        .eq("id", selectedPickup.id);

      if (error) throw error;

      // Update packages based on action
      let packageStatus = "scheduled"; // default
      if (selectedAction === "completed") {
        packageStatus = "picked_up";
      } else if (selectedAction === "missed" || selectedAction === "cancelled") {
        // Unlock packages for rescheduling
        await (supabase as any)
          .from("packages")
          .update({ pickup_id: null, status: "approved" })
          .eq("pickup_id", selectedPickup.id);
      } else if (selectedAction === "no_show") {
        // Keep packages linked but mark for next period
        packageStatus = "pending";
      }

      if (selectedAction === "completed") {
        await (supabase as any)
          .from("packages")
          .update({ status: packageStatus })
          .eq("pickup_id", selectedPickup.id);
      }

      const messages: Record<string, string> = {
        completed: "Pickup marked as completed. Packages are now picked up.",
        missed: "Pickup marked as missed. User can reschedule without penalty.",
        no_show: "Pickup marked as no-show. This counts toward user's monthly limit.",
        cancelled: "Pickup cancelled. User's pickup slot has been refunded.",
      };

      toast.success(messages[selectedAction] || "Pickup updated.");
      setActionDialogOpen(false);
      setSelectedPickup(null);
      await fetchConfirmedPickups();
      onPickupUpdated?.();
    } catch (error: any) {
      toast.error(error.message || "Failed to update pickup");
    } finally {
      setActionLoading(false);
    }
  };

  const getPickupDateStatus = (pickup: PickupWithDetails) => {
    const pickupDate = new Date(pickup.proposed_date);
    if (isToday(pickupDate)) return "today";
    if (isPast(pickupDate)) return "past";
    return "future";
  };

  if (loading || pickups.length === 0) {
    return null;
  }

  // Separate pickups by date status
  const todayPickups = pickups.filter((p) => getPickupDateStatus(p) === "today");
  const pastPickups = pickups.filter((p) => getPickupDateStatus(p) === "past");
  const futurePickups = pickups.filter((p) => getPickupDateStatus(p) === "future");

  return (
    <div className="space-y-6">
      {/* Today's Pickups */}
      {todayPickups.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-lg">Today's Pickups</h3>
            <Badge className="bg-primary">{todayPickups.length}</Badge>
          </div>
          <div className="grid gap-4">
            {todayPickups.map((pickup) => (
              <PickupCard 
                key={pickup.id} 
                pickup={pickup} 
                onAction={handleOpenAction}
                formatRetailerName={formatRetailerName}
                variant="today"
              />
            ))}
          </div>
        </div>
      )}

      {/* Past Due Pickups */}
      {pastPickups.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <h3 className="font-semibold text-lg">Past Due Pickups</h3>
            <Badge variant="destructive">{pastPickups.length}</Badge>
          </div>
          <div className="grid gap-4">
            {pastPickups.map((pickup) => (
              <PickupCard 
                key={pickup.id} 
                pickup={pickup} 
                onAction={handleOpenAction}
                formatRetailerName={formatRetailerName}
                variant="past"
              />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Pickups */}
      {futurePickups.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-semibold text-lg">Upcoming Pickups</h3>
            <Badge variant="secondary">{futurePickups.length}</Badge>
          </div>
          <div className="grid gap-4">
            {futurePickups.map((pickup) => (
              <PickupCard 
                key={pickup.id} 
                pickup={pickup} 
                onAction={handleOpenAction}
                formatRetailerName={formatRetailerName}
                variant="future"
              />
            ))}
          </div>
        </div>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Pickup Status</DialogTitle>
            <DialogDescription>
              {selectedPickup && (
                <>Choose an action for {selectedPickup.userName}'s pickup.</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={selectedAction} onValueChange={setSelectedAction}>
                <SelectTrigger>
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Mark Completed
                    </div>
                  </SelectItem>
                  <SelectItem value="missed">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      Mark as Missed (no penalty)
                    </div>
                  </SelectItem>
                  <SelectItem value="no_show">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4 text-red-600" />
                      User No-Show (counts toward limit)
                    </div>
                  </SelectItem>
                  <SelectItem value="cancelled">
                    <div className="flex items-center gap-2">
                      <Ban className="h-4 w-4 text-gray-600" />
                      Cancel Pickup (refund slot)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Action explanations */}
            {selectedAction && (
              <div className="p-3 rounded-lg bg-muted text-sm">
                {selectedAction === "completed" && (
                  <p>All packages will be marked as "picked up" and ready for return processing.</p>
                )}
                {selectedAction === "missed" && (
                  <p>Packages will be unlocked for rescheduling. This does NOT count toward the user's monthly limit.</p>
                )}
                {selectedAction === "no_show" && (
                  <p>User was not available. This DOES count toward their monthly pickup limit.</p>
                )}
                {selectedAction === "cancelled" && (
                  <p>Pickup slot will be refunded. Packages will be unlocked for rescheduling.</p>
                )}
              </div>
            )}

            {selectedAction === "cancelled" && (
              <div className="space-y-2">
                <Label>Cancellation Reason (Optional)</Label>
                <Textarea
                  placeholder="Enter reason for cancellation..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  maxLength={500}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmAction} 
              disabled={!selectedAction || actionLoading}
            >
              {actionLoading ? "Updating..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-component for pickup card
const PickupCard = ({ 
  pickup, 
  onAction, 
  formatRetailerName,
  variant 
}: { 
  pickup: PickupWithDetails; 
  onAction: (pickup: PickupWithDetails) => void;
  formatRetailerName: (retailer: string) => string;
  variant: "today" | "past" | "future";
}) => {
  const borderClass = {
    today: "border-primary/30",
    past: "border-destructive/30",
    future: "border-muted",
  }[variant];

  return (
    <Card className={cn("", borderClass)}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">{pickup.userName}</CardTitle>
              <p className="text-sm text-muted-foreground">{pickup.userEmail}</p>
            </div>
          </div>
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
            Confirmed
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Date/Time */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
            <span>{format(new Date(pickup.proposed_date), "MMM d, yyyy")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span>{pickup.proposed_time_slot}</span>
          </div>
        </div>

        {/* Address */}
        <div className="flex items-start gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
          <span className="text-muted-foreground">{pickup.address}</span>
        </div>

        {/* Packages */}
        <div className="flex items-center gap-2 flex-wrap">
          <Package className="h-4 w-4 text-muted-foreground" />
          {pickup.packages.map((pkg) => (
            <Badge key={pkg.id} variant="secondary" className="text-xs">
              {formatRetailerName(pkg.retailer)}
            </Badge>
          ))}
        </div>

        {/* Action Button */}
        <div className="pt-2">
          <Button 
            size="sm" 
            onClick={() => onAction(pickup)}
            className="w-full sm:w-auto"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Update Status
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
